// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { deriveRoleBucket } from './roleBuckets'
import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react'
import * as api from '../api/client'
import { resolvePharmacyFromUrl } from './pharmacyUrl'
import { clearStickyViewState } from '../components/useStickyState'
import { clearAllCarePlanDrafts } from '../pages/carerx/carePlanDraft'

// ----------------------------------------------------------------------
// Legacy role labels — kept for backward-compat with App.tsx + StubDashboard
// during the WI-007 / WI-008 transition. Once the AppShell rewrite lands
// in WI-008 and StubDashboard is replaced, this whole concept goes away.
// ----------------------------------------------------------------------

// 'manufacturer' (D-MFR-1) is an EXTERNAL role. It is added here only so the router
// can gate /manufacturer/*; it is deliberately NOT added to shell/navConfig's Role,
// because that type drives the pharmacy navigation and an external party must never
// appear in, or be routed through, the pharmacy shell.
export type Role = 'pharmacy_staff' | 'provider' | 'provider_nurse' | 'patient' | 'admin' | 'manufacturer' | 'support' | 'facility'

/**
 * The portal name shown in the shell chrome (TopBar / Sidebar), keyed off the viewer's role:
 *   - provider / provider_nurse → "Provider Portal" (provider view)
 *   - patient                   → "Patient Portal"
 *   - pharmacy_staff / admin / pre-role → "Pharmacy Portal" (internal view)
 * Replaces the former single "OwlRx Portal" brand so each audience sees their own portal name.
 */
export function portalBrandLabel(role: Role | null | undefined): string {
  if (role === 'facility') return 'Facility Portal'
  if (role === 'provider' || role === 'provider_nurse') return 'Provider Portal'
  if (role === 'patient') return 'Patient Portal'
  if (role === 'manufacturer') return 'Manufacturer Portal'
  if (role === 'support') return 'Support Portal'
  return 'Pharmacy Portal'
}

/**
 * Map a user's permission set to the legacy Role enum the rest of the
 * app still uses. We pick the highest-privilege bucket present; unknowns
 * fall back to null. Once WI-008 replaces StubDashboard + App.tsx,
 * this whole concept goes away.
 */
function legacyRoleFromPermissions(perms: string[]): Role | null {
  // D-RBAC-2: the derivation lives in roleBuckets.ts, mirrored from functions/Auth/RoleBuckets.cs and
  // pinned by a parity test on the server side (L-068). Do not add branches here.
  return deriveRoleBucket(perms)
}

// Public Me shape (matches /api/auth/me response)
export interface Me {
  authenticated: boolean
  account_id?: string
  email?: string
  display_name?: string
  pharmacy_id?: string | null
  pharmacy_slug?: string | null
  is_synthetic?: boolean
  permissions: string[]
  primary_role_id?: string | null
  role_name?: string | null
  role_ids?: string[]
  session_expires_at?: string
  unread_notifications?: number
  /** D-PMS-1: this tenant's dispensing system, so vendor-specific copy names the
   *  system the pharmacy actually runs. Null/absent = unknown, and the UI stays
   *  neutral rather than asserting a vendor. */
  pms_vendor?: string | null
  pms_display_name?: string | null
}

interface AuthState {
  // New API
  me: Me | null
  loading: boolean
  signIn: (email: string, password: string, pharmacySlug?: string) => Promise<{ ok: boolean; error?: string; mfaRequired?: boolean; mfaEnrollmentRequired?: boolean; mfaToken?: string }>
  verifyMfa: (mfaToken: string, code: string) => Promise<{ ok: boolean; error?: string }>
  signOut: () => Promise<void>
  hasPermission: (key: string) => boolean
  refresh: () => Promise<void>

  // Legacy compatibility shim — derived from `me`
  role: Role | null
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null)
  const [loading, setLoading] = useState(true)
  // Set true once bootstrap (incl. the cookie-mint for deep-link tabs) has fully resolved. The
  // background focus/interval refreshes below must NOT run before this, or they race the mint —
  // calling /auth/me with no token yet, reading logged-out, and clearing the token (which briefly
  // shows the page then bounces to /login). This was the email deep-link "flashes then redirects" bug.
  const bootstrappedRef = useRef(false)

  const refresh = useCallback(async () => {
    try {
      const fresh = await api.getAuthMe()
      setMe(fresh)
      // D-EMAIL-1: remember the actor's (non-PHI) pharmacy slug from the live session, so a later
      // expired-session email link routes to THIS pharmacy's branded login — even from a tab that
      // never navigated a /p/{slug} URL itself.
      try {
        const slug = (fresh as any).pharmacy_slug
        if (fresh.authenticated && slug) localStorage.setItem('owlrx_pharmacy_hint', String(slug))
      } catch { /* storage unavailable */ }
      // Session expired (a definitive server response, not a transient error): drop the dead
      // per-tab token so the next SSO can re-mint cleanly and stale credentials aren't reused.
      if (!fresh.authenticated) api.setSessionToken(null)
    } catch {
      setMe({ authenticated: false, permissions: [] })
    } finally {
      setLoading(false)
    }
  }, [])

  // Bootstrap: the tab that initiated SSO (owlrx_sso_pending) mints a per-tab token from the SSO
  // cookie. A fresh tab opened on a protected DEEP-LINK (e.g. an email document link) also mints from
  // the cookie — it has its own per-tab token store, so without this it reads as logged out despite a
  // valid session and bounces to /login. The backend still authorizes every request against the
  // cookie's identity, so this cannot expose another pharmacy's data; a wrong/expired cookie simply
  // fails the mint and the tab stays logged out. Bare/public paths keep the token-only default.
  const bootstrap = useCallback(async () => {
    try {
      let pending = false
      try { pending = !!sessionStorage.getItem('owlrx_sso_pending') } catch {}
      const hasToken = !!api.getSessionToken()
      const path = window.location.pathname
      // Public/auth screens keep the token-only default; anything else is treated as a deep-link.
      // Exact match for the apex routes; suffix match so basename-prefixed /p/{slug}/login etc. count.
      const onDeepLink = !(
        ['/', '/login', '/forgot-password', '/reset-password', '/accept-invitation'].includes(path) ||
        /\/(login|forgot-password|reset-password|accept-invitation)$/.test(path)
      )
      // On an SSO return, always mint a FRESH per-tab token — overwriting any stale token left over
      // from an expired session. (Previously this skipped minting when a stale token was present,
      // which left the tab stuck on a dead token until it was closed.)
      if (pending || (!hasToken && onDeepLink)) {
        try {
          const t = await api.mintSessionToken()
          if (t.session_token) api.setSessionToken(t.session_token)
        } catch { /* no valid SSO cookie; stay logged out */ }
      }
      try { sessionStorage.removeItem('owlrx_sso_pending') } catch {}
    } finally {
      await refresh()
      bootstrappedRef.current = true
    }
  }, [refresh])

  useEffect(() => { bootstrap() }, [bootstrap])

  // Keep `me` (notably unread_notifications count) fresh.
  // - Refresh on window focus
  // - Refresh every 60s while the tab is visible
  useEffect(() => {
    const onFocus = () => { if (bootstrappedRef.current && document.visibilityState === 'visible') refresh() }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onFocus)
    const id = window.setInterval(() => {
      if (bootstrappedRef.current && document.visibilityState === 'visible') refresh()
    }, 60_000)
    return () => {
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onFocus)
      window.clearInterval(id)
    }
  }, [refresh])

  const signIn = useCallback(async (email: string, password: string, pharmacySlug?: string) => {
    try {
      // §2c: when logging in from a pharmacy URL (subdomain or /p/{id}), pass the slug so the API
      // resolves + verifies credentials against that pharmacy's own DB. Prefer an explicitly resolved
      // slug (the caller may have resolved an opaque url_code to its real slug); else derive from URL.
      const urlId = typeof window !== 'undefined'
        ? resolvePharmacyFromUrl(window.location.hostname, window.location.pathname).slug
        : null
      const slug = pharmacySlug ?? urlId
      const res = await api.authLogin({ email, password, ...(slug ? { pharmacy_slug: slug } : {}) })
      if (res.mfa_required && res.mfa_token) {
        // Password was correct but a second factor is required — don't refresh yet.
        return { ok: false, mfaRequired: true, mfaToken: res.mfa_token }
      }
      if (res.mfa_enrollment_required && res.mfa_token) {
        // Admin-required MFA but not enrolled yet — must set up TOTP before a session is minted.
        return { ok: false, mfaEnrollmentRequired: true, mfaToken: res.mfa_token }
      }
      if (res.session_token) api.setSessionToken(res.session_token) // T2: per-tab token
      // Signing IN is the boundary that always happens. signOut can be skipped entirely — a session
      // idles out after 15 minutes and the next person simply logs in on the same tab — so clearing
      // only there would still hand them the previous user's filters.
      clearStickyViewState()
      clearAllCarePlanDrafts() // unsaved care-plan goals are clinical text; never inherited by the next user of this tab
      await refresh()
      return { ok: true }
    } catch (e: any) {
      const code = e?.body?.error || e?.message || 'login_failed'
      return { ok: false, error: code }
    }
  }, [refresh])

  const verifyMfa = useCallback(async (mfaToken: string, code: string) => {
    try {
      const res = await api.mfaVerify(mfaToken, code)  // sets the session cookie on success
      if (res?.session_token) api.setSessionToken(res.session_token) // T2: per-tab token
      await refresh()
      return { ok: true }
    } catch (e: any) {
      const code2 = e?.body?.error || e?.message || 'invalid_code'
      return { ok: false, error: code2 }
    }
  }, [refresh])

  const signOut = useCallback(async () => {
    try { await api.authLogout() } catch { /* best-effort */ }
    api.setSessionToken(null) // T2: clear this tab's token
    // Sticky view filters can hold patient search terms and are keyed by VIEW, not by user. The tab
    // outlives the login, so on a shared pharmacy workstation the next person to sign in would
    // otherwise inherit them. See clearStickyViewState.
    clearStickyViewState()
    clearAllCarePlanDrafts()
    setMe({ authenticated: false, permissions: [] })
  }, [])

  const hasPermission = useCallback(
    (key: string) => Boolean(me?.authenticated && me.permissions.includes(key)),
    [me]
  )

  // Legacy `role` derived from permissions
  const role: Role | null = me?.authenticated
    ? legacyRoleFromPermissions(me.permissions || [])
    : null

  return (
    <AuthContext.Provider value={{ me, loading, signIn, verifyMfa, signOut, hasPermission, refresh, role }}>
      {children}
    </AuthContext.Provider>
  )
}

/**
 * D-PMS-1. The name of THIS tenant's dispensing system, for copy that used to say
 * "PioneerRx" unconditionally. Falls back to neutral wording rather than guessing a
 * vendor — a tenant whose PMS we don't know should not be told it runs one.
 */
export function usePmsName(): { name: string; known: boolean } {
  const { me } = useAuth()
  const label = me?.pms_display_name?.trim() || null
  return label ? { name: label, known: true } : { name: 'your dispensing system', known: false }
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
